import { clamp, ConvexArea, Point, Region } from 'core/geometry';
import { CropHandlePosition, getOppositeCorners } from './crop-handle-corner';

export class RegionScaler
{
  public readonly samePoint: Point;

  private readonly origin: Point;
  private readonly diagonal: Point;
  private readonly minFactor: number;
  private readonly maxFactor: number;

  constructor(
      original: Region,
      minSize: Region,
      constrainArea: ConvexArea,
      position: CropHandlePosition)
  {
    const corners = getOppositeCorners(original, position);

    this.samePoint = corners.same;
    this.origin = corners.opposite;

    this.diagonal = new Point(
        corners.same.x - corners.opposite.x,
        corners.same.y - corners.opposite.y);

    this.minFactor = Math.max(
        this.factorOfLength(minSize.width, this.diagonal.x),
        this.factorOfLength(minSize.height, this.diagonal.y));

    this.maxFactor =
        constrainArea.maxRectangleFactor(this.origin, this.diagonal);
  }

  public applyScalePoint(fallback: Region, point: Point): Region
  {
    if (this.maxFactor < this.minFactor) {
      return fallback;
    }

    const factor =
        clamp(this.factorOf(point), this.minFactor, this.maxFactor);

    return Region.fromTwoPoints(
        this.origin,
        new Point(
            this.origin.x + factor * this.diagonal.x,
            this.origin.y + factor * this.diagonal.y));
  }

  private factorOf(point: Point): number
  {
    const along = (distance: number, extent: number): number =>
        extent === 0 ? Infinity : distance / extent;

    return Math.min(
        along(point.x - this.origin.x, this.diagonal.x),
        along(point.y - this.origin.y, this.diagonal.y));
  }

  private factorOfLength(length: number, extent: number): number
  {
    return extent === 0 ? 0 : length / Math.abs(extent);
  }
}
